import {gql} from '@apollo/client';

/**
 * Support Performance: helpdesk analytics (sassandra).
 *
 * The team, agent and export operations are SUPERADMIN-only; the personal one
 * (`getMySupportPerformance`) is open to CUSTOMER_SUPPORT too. All return an
 * Error/Response union, so callers branch on `__typename`, and a screen never
 * mounts a query its viewer can't run.
 */

const METRICS = gql`
  fragment SupportPerformanceMetricsFields on SupportPerformanceMetrics {
    opened
    resolved
    abandoned
    pending
    unassignedQueue
    averageRating
    ratingCount
    ratingCoverage
    averageAgentSeconds
    medianAgentSeconds
    agentTimeCount
    averageCustomerWaitSeconds
    medianCustomerWaitSeconds
    customerTimeCount
    ratingDistribution {
      oneStar
      twoStars
      threeStars
      fourStars
      fiveStars
    }
  }
`;

// Queue entry to the first customer-visible human reply, against the one
// global target. `status` is the backend's verdict; never re-derive it.
const FIRST_RESPONSE = gql`
  fragment SupportFirstResponseFields on SupportFirstResponseMetrics {
    targetSeconds
    averageSeconds
    medianSeconds
    measuredTickets
    unansweredTickets
    overdueUnansweredTickets
    status
  }
`;

const BENCHMARKS = gql`
  fragment SupportBenchmarkFields on SupportTeamBenchmarks {
    agentCount
    resolvedCount
    averageResolved
    resolvedRank
    resolvedShare
    averageRating
    ratingCount
    ratingCoverage
    averageCustomerWaitSeconds
    customerTimeCount
  }
`;

const TICKET = gql`
  fragment SupportTicketFields on SupportPerformanceTicket {
    id
    customer {
      id
      firstName
      lastName
    }
    category
    issue
    status
    openedAt
    resolvedAt
    customerWaitSeconds
    agentSeconds
    openSeconds
    rating
    comment
  }
`;

const TREND = gql`
  fragment SupportTrendFields on SupportPerformanceTrend {
    from
    resolved
    averageRating
    ratingCount
    ratingCoverage
    averageCustomerWaitSeconds
    averageAgentSeconds
  }
`;

const RANKING = gql`
  ${METRICS}
  fragment SupportPerformanceRankingFields on SupportPerformanceRanking {
    id
    label
    categoryId
    categoryLabel
    featureId
    featureLabel
    language
    product
    count
    percentage
    previousCount
    changePercentage
    metrics {
      ...SupportPerformanceMetricsFields
    }
  }
`;

export const GET_SUPPORT_PERFORMANCE = gql`
  ${RANKING}
  ${FIRST_RESPONSE}
  query GetSupportPerformance($input: SupportPerformanceInput!) {
    getSupportPerformance(input: $input) {
      __typename
      ... on Error {
        message
        statusCode
      }
      ... on ResponseWithSupportPerformance {
        message
        data {
          exportRequested
          from
          to
          previousFrom
          pendingAsOf
          firstResponse {
            ...SupportFirstResponseFields
          }
          availableAgents {
            id
            firstName
            lastName
          }
          overview {
            ...SupportPerformanceMetricsFields
          }
          closedBy {
            human
            ai
            faq
            customerNoHelp
          }
          agents {
            agentId
            agent {
              id
              firstName
              lastName
            }
            metrics {
              ...SupportPerformanceMetricsFields
            }
            firstResponse {
              ...SupportFirstResponseFields
            }
          }
          unattributed {
            agentId
            metrics {
              ...SupportPerformanceMetricsFields
            }
            firstResponse {
              ...SupportFirstResponseFields
            }
          }
          categories {
            ...SupportPerformanceRankingFields
          }
          features {
            ...SupportPerformanceRankingFields
          }
          issues {
            ...SupportPerformanceRankingFields
          }
        }
      }
    }
  }
`;

// Fields the agent detail and the personal view share.
const DETAIL_FIELDS = `
  from
  to
  pendingAsOf
  agentId
  firstResponse {
    ...SupportFirstResponseFields
  }
  metrics {
    ...SupportPerformanceMetricsFields
  }
  benchmarks {
    ...SupportBenchmarkFields
  }
  categories {
    ...SupportPerformanceRankingFields
  }
  issues {
    ...SupportPerformanceRankingFields
  }
  trends {
    ...SupportTrendFields
  }
  tickets {
    ...SupportTicketFields
  }
  pagination {
    page
    limit
    total
    pages
  }
`;

export const GET_SUPPORT_AGENT_PERFORMANCE = gql`
  ${RANKING}
  ${FIRST_RESPONSE}
  ${BENCHMARKS}
  ${TREND}
  ${TICKET}
  query GetSupportAgentPerformance($input: SupportAgentPerformanceInput!) {
    getSupportAgentPerformance(input: $input) {
      __typename
      ... on Error {
        message
        statusCode
      }
      ... on ResponseWithSupportAgentPerformance {
        message
        data {
          ${DETAIL_FIELDS}
          agent {
            id
            firstName
            lastName
          }
          availableAgents {
            id
            firstName
            lastName
          }
        }
      }
    }
  }
`;

/**
 * The signed-in agent's own numbers. The server picks the agent from the
 * session: never send an agent id, and there is no roster to choose from.
 * Open to CUSTOMER_SUPPORT and SUPERADMIN.
 */
export const GET_MY_SUPPORT_PERFORMANCE = gql`
  ${RANKING}
  ${FIRST_RESPONSE}
  ${BENCHMARKS}
  ${TREND}
  ${TICKET}
  query GetMySupportPerformance($input: MySupportPerformanceInput!) {
    getMySupportPerformance(input: $input) {
      __typename
      ... on Error {
        message
        statusCode
      }
      ... on ResponseWithMySupportPerformance {
        message
        data {
          ${DETAIL_FIELDS}
        }
      }
    }
  }
`;

/**
 * A second, lighter read of the personal endpoint for one ticket list (the
 * feedback list), so it pages on its own.
 */
export const GET_MY_SUPPORT_TICKETS = gql`
  ${TICKET}
  query GetMySupportTickets($input: MySupportPerformanceInput!) {
    getMySupportPerformance(input: $input) {
      __typename
      ... on Error {
        message
        statusCode
      }
      ... on ResponseWithMySupportPerformance {
        data {
          tickets {
            ...SupportTicketFields
          }
          pagination {
            page
            limit
            total
            pages
          }
        }
      }
    }
  }
`;

/**
 * Every ticket matching the visible list's filters as one CSV (page and limit
 * are ignored). Over 10,000 rows comes back as a 400 asking to narrow down.
 */
export const EXPORT_SUPPORT_AGENT_TICKETS = gql`
  query ExportSupportAgentTickets($input: SupportAgentPerformanceInput!) {
    exportSupportAgentTickets(input: $input) {
      __typename
      ... on Error {
        message
        statusCode
      }
      ... on ResponseWithSupportTicketExport {
        message
        data {
          filename
          csv
          rowCount
        }
      }
    }
  }
`;
